/* Turns the chart canvas into a PNG or a PDF.
   Both libraries are heavy and only needed when someone actually exports, so
   they are imported on demand rather than bundled into the page load. */

const FILE_STAMP = () => new Date().toISOString().slice(0, 10);

async function snapshot(node) {
  const { default: html2canvas } = await import('html2canvas');

  /* html2canvas rebuilds the page in a detached clone, where two things in
     this layout cut the bottom off:
       - the canvas is sized by `aspect-ratio`, which the clone does not
         resolve, so its height collapses;
       - the heat map and the donut legends scroll inside their cells, and
         only the visible slice is drawn.
     The export class pins an explicit height and unclips the scrollers, and
     the measurement is taken after the browser has applied it. */
  node.classList.add('exportingCanvas');
  node.style.height = `${node.scrollHeight}px`;
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  try {
    return await html2canvas(node, {
      // The page background, so the export is not transparent where cards end.
      backgroundColor: '#ffffff',
      // Twice the screen resolution, so text in the PDF is not soft.
      scale: Math.min(2, (window.devicePixelRatio || 1) * 2),
      useCORS: true,
      logging: false,
      width: Math.ceil(node.scrollWidth),
      height: Math.ceil(node.scrollHeight),
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight
    });
  } finally {
    node.style.height = '';
    node.classList.remove('exportingCanvas');
  }
}

function triggerDownload(href, filename) {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadPng(node, name) {
  const canvas = await snapshot(node);
  triggerDownload(canvas.toDataURL('image/png'), `${name}-${FILE_STAMP()}.png`);
}

export async function downloadPdf(node, name, title) {
  const canvas = await snapshot(node);
  const { jsPDF } = await import('jspdf');

  // A4 landscape suits a 16:9 canvas far better than portrait.
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const headerHeight = title ? 30 : 0;

  if (title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(76, 35, 101);
    pdf.text(title, margin, margin + 6);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 130);
    pdf.text(
      new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
      pageWidth - margin,
      margin + 6,
      { align: 'right' }
    );
  }

  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - margin * 2 - headerHeight;
  // Fit inside the page without distorting the 16:9 shape.
  const ratio = Math.min(availableWidth / canvas.width, availableHeight / canvas.height);
  const drawWidth = canvas.width * ratio;
  const drawHeight = canvas.height * ratio;

  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    margin + (availableWidth - drawWidth) / 2,
    margin + headerHeight,
    drawWidth,
    drawHeight
  );

  pdf.save(`${name}-${FILE_STAMP()}.pdf`);
}
